import { useDataProvider } from "src/system/useDataProvider.ts";
import {
  type DefaultError,
  useMutation,
  type UseMutationOptions,
  useQueryClient,
} from "@tanstack/react-query";
// todo: useCreateOne hook should not depend on useResourceForm hook
import type {
  ValidRequestData,
  ValidSaveFormResponseData,
} from "src/components/Form/hooks/useResourceForm/useResourceForm.tsx";
import { useResourceQueries } from "src/framework/useResourceQueries.ts";
import { useSnackbar } from "src/App/App.hooks.ts";
import { logger } from "src/logger";
import { useTranslation } from "react-i18next";
import { getResourceI18nNamespace } from "src/system/getResourceI18nNamespace.ts";

export const useCreateOne = <
  RequestData extends ValidRequestData = ValidRequestData,
  Data extends ValidSaveFormResponseData = ValidSaveFormResponseData,
>({
  resource,
  errorNotification: hookErrorNotification,
  successNotification: hookSuccessNotification,
  additionalHeaders,
  meta,
  ...options
}: {
  resource: string;
  meta?: any;
  errorNotification?: {
    message: string;
    flash?: boolean;
  };
  additionalHeaders?: Record<string, any>;
  successNotification?: {
    message?: string;
    flash?: boolean;
  };
} & UseMutationOptions<
  Data,
  DefaultError,
  {
    data: RequestData;
    locale: string;
    errorNotification?: {
      message: string;
      flash?: boolean;
    };
    successNotification?: {
      message?: string;
      flash?: boolean;
    };
    disableSuccessNotification?: boolean;
    disableErrorNotification?: boolean;
  }
>) => {
  const queryClient = useQueryClient();
  const dataProvider = useDataProvider();
  const snackbar = useSnackbar();
  const queries = useResourceQueries();
  const { t } = useTranslation();
  return useMutation({
    mutationKey: [resource, "createOne"],
    mutationFn: async ({ data, locale }) => {
      try {
        return await dataProvider.createOne({
          resource: resource,
          data: data,
          additionalHeaders,
          locale,
          meta,
        });
      } catch (error: any) {
        logger.error(error);
        throw error;
      }
    },
    ...options,
    onError: (data, variables, context) => {
      const { errorNotification, disableErrorNotification } = variables;
      if (!disableErrorNotification) {
        // todo: translate default notification
        const { message = "Couldn't create the record", flash = true } =
          hookErrorNotification || errorNotification || {};
        snackbar.error({
          flash,
          message,
        });
      }
      options?.onError?.(data, variables, context);
    },
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({
        queryKey: queries.lists({
          resource,
        }),
      });
      const { successNotification, disableSuccessNotification } = variables;
      if (!disableSuccessNotification) {
        const {
          flash = true,
          message = t(`notifications.created`, "Record has been created", {
            ns: getResourceI18nNamespace(resource),
          }),
        } = hookSuccessNotification || successNotification || {};
        snackbar.success({
          flash,
          message,
        });
      }
      options?.onSuccess?.(data, variables, context);
    },
  });
};
